import { useMemo } from 'react';
import { OnboardingData } from '../types';

export const useOnboardingCalculations = (onboardingData: OnboardingData | null) => {
  const progressPercentage = useMemo(() => {
    if (!onboardingData) return 0;
    
    const requiredTasks = onboardingData.tasks.filter(t => t.taskType === 'required');
    const completedRequired = onboardingData.progress.filter(
      p => p.status === 'completed' && 
      requiredTasks.some(t => t.id === p.taskId)
    ).length;
    
    // Ensure we never exceed 100% and handle edge cases
    if (requiredTasks.length === 0) return 0;
    const percentage = (Math.min(completedRequired, requiredTasks.length) / requiredTasks.length) * 100;
    return Math.min(100, Math.round(percentage));
  }, [onboardingData]);

  const completedTasksCount = useMemo(() => {
    if (!onboardingData) return 0;
    // Ensure we don't count more completed tasks than total available tasks
    const completedCount = onboardingData.progress.filter(p => p.status === 'completed').length;
    const totalTasks = onboardingData.tasks.length;
    return Math.min(completedCount, totalTasks);
  }, [onboardingData]);

  const taskStats = useMemo(() => {
    if (!onboardingData) return { required: 0, recommended: 0, optional: 0, total: 0 };
    
    const tasks = onboardingData.tasks;
    return {
      required: tasks.filter(t => t.taskType === 'required').length,
      recommended: tasks.filter(t => t.taskType === 'recommended').length,
      optional: tasks.filter(t => t.taskType === 'optional').length,
      total: tasks.length,
    };
  }, [onboardingData]);

  const milestoneReached = useMemo(() => ({
    twentyFive: progressPercentage >= 25,
    fifty: progressPercentage >= 50,
    seventyFive: progressPercentage >= 75,
    hundred: progressPercentage >= 100,
  }), [progressPercentage]);

  return {
    progressPercentage,
    completedTasksCount,
    taskStats,
    milestoneReached,
  };
};